import * as React from "react"
import { Link, useHistory, useLocation } from "react-router-dom"

import { AuthBrandHero } from "@/components/auth-brand-hero"
import {
  authSplitCrossState,
  authSplitPath,
  AUTH_SPLIT_LEG_MS,
  type AuthSplitLocationState,
  type AuthSplitVariant,
} from "@/components/auth-split-transition"
import { CardContent } from "@/components/ui/card"
import { AUTH_SPLIT_CARD_MIN_H_CLASS } from "@/components/auth-split-card-layout"
import { cn } from "@/lib/utils"

type AuthMirroredSplitShellProps = {
  variant: AuthSplitVariant
  children: React.ReactNode
  /** Verification (and similar) — form spans the full card, hero is omitted. */
  fullWidthForm?: boolean
  className?: string
}

/** Halfway state: hero and form sit on the same half, hero covering the form. */
type AuthSplitLayout = AuthSplitVariant | "crossing"

/** Returns false when the route should just navigate: mobile card, or reduced motion. */
function splitAnimationEnabled(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false
  }
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false
  return window.matchMedia("(min-width: 768px)").matches
}

const AuthSplitCrossContext = React.createContext<
  ((target: AuthSplitVariant) => boolean) | null
>(null)

/**
 * Sign-in ↔ sign-up switch link. Runs the first leg of the criss-cross on the
 * current page and defers the route change to the crossing point, so the form
 * content only swaps while it is hidden behind the hero.
 */
export function AuthSplitSwitchLink({
  to,
  children,
  className,
  ...props
}: {
  to: AuthSplitVariant
  children: React.ReactNode
  className?: string
} & Omit<React.ComponentProps<typeof Link>, "to" | "children" | "className">) {
  const crossTo = React.useContext(AuthSplitCrossContext)

  return (
    <Link
      to={authSplitPath(to)}
      className={className}
      {...props}
      onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return
        }
        if (crossTo?.(to)) event.preventDefault()
      }}
    >
      {children}
    </Link>
  )
}

/**
 * Shared sign-in / sign-up card body.
 *
 * Desktop: hero + form are absolute halves that swap sides (criss-cross). The
 * slide runs in two legs — the outgoing page animates to the crossing point,
 * then the incoming page mounts there and animates out to its own layout.
 * Mobile: form only (hero stays hidden).
 */
export function AuthMirroredSplitShell({
  variant,
  children,
  fullWidthForm = false,
  className,
}: AuthMirroredSplitShellProps) {
  const location = useLocation<AuthSplitLocationState | undefined>()
  const history = useHistory()
  const enteringAtCrossing = React.useRef(
    Boolean(location.state?.authSplitCrossed) && splitAnimationEnabled()
  )

  const [layout, setLayout] = React.useState<AuthSplitLayout>(() =>
    enteringAtCrossing.current ? "crossing" : variant
  )
  const leavingRef = React.useRef(false)
  const handoffRef = React.useRef<number>()

  // Second leg: released a frame after the crossing state paints, so the
  // browser has a start position to transition from.
  React.useEffect(() => {
    if (!enteringAtCrossing.current) return
    enteringAtCrossing.current = false
    let cancelled = false
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) setLayout(variant)
      })
    })
    return () => {
      cancelled = true
      cancelAnimationFrame(frame)
    }
  }, [variant])

  React.useEffect(() => {
    return () => {
      if (handoffRef.current !== undefined) window.clearTimeout(handoffRef.current)
    }
  }, [])

  /** True when the link click was handled here and should not navigate itself. */
  const crossTo = React.useCallback(
    (target: AuthSplitVariant) => {
      if (leavingRef.current) return true
      if (!splitAnimationEnabled()) return false

      leavingRef.current = true
      setLayout("crossing")
      handoffRef.current = window.setTimeout(() => {
        history.push(authSplitPath(target), authSplitCrossState())
      }, AUTH_SPLIT_LEG_MS)
      return true
    },
    [history]
  )

  if (fullWidthForm) {
    return (
      <AuthSplitCrossContext.Provider value={crossTo}>
        <CardContent
          className={cn("grid w-full p-0", className)}
          data-auth-mirrored-split=""
          data-auth-mirrored-full=""
        >
          <div className="min-w-0 w-full">{children}</div>
        </CardContent>
      </AuthSplitCrossContext.Provider>
    )
  }

  return (
    <AuthSplitCrossContext.Provider value={crossTo}>
      <CardContent
        className={cn("relative w-full overflow-hidden p-0", AUTH_SPLIT_CARD_MIN_H_CLASS, className)}
        data-auth-mirrored-split=""
        data-auth-layout={layout}
      >
        <div
          data-auth-mirrored-hero
          className="pointer-events-none absolute inset-y-0 left-0 hidden w-1/2 md:block"
          aria-hidden
        >
          <AuthBrandHero className="!block h-full min-h-full" />
        </div>
        <div data-auth-mirrored-form className="relative min-w-0 w-full md:absolute md:inset-y-0 md:left-0 md:w-1/2">
          {children}
        </div>
      </CardContent>
    </AuthSplitCrossContext.Provider>
  )
}
